/* Public Open Play RSVP page — accept/decline invitation by token.
   Mirrors SportsRsvpPage.jsx but for individual player registration into an
   Open Play (rotation) session. */
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import errText from '../../lib/errText';
import { Zap, CheckCircle, XCircle, Clock, AlertCircle } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;
const RED = '#E63946';

const inputStyle = {
  width: '100%', padding: '11px 13px', borderRadius: 8, background: '#0A0A0A',
  border: '1px solid #2A2A2A', color: '#fff', fontSize: 14, outline: 'none', marginBottom: 8,
};

const STATUS_UI = {
  pending:   { color: RED, bg: 'rgba(230,57,70,0.14)', label: 'Pending Response', icon: Clock },
  accepted:  { color: '#22C55E', bg: '#22C55E14', label: 'Accepted', icon: CheckCircle },
  declined:  { color: '#EF4444', bg: '#EF444414', label: 'Declined', icon: XCircle },
  expired:   { color: '#737373', bg: '#1A1A1A',   label: 'Expired', icon: AlertCircle },
  cancelled: { color: '#737373', bg: '#1A1A1A',   label: 'Cancelled', icon: XCircle },
};

export default function SportsOpenPlayRsvpPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [responding, setResponding] = useState(false);
  const [done, setDone] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [orgLabel, setOrgLabel] = useState('');
  const [divId, setDivId] = useState('');
  const [avatarKey, setAvatarKey] = useState(null);
  const [avatarThumbKey, setAvatarThumbKey] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [avatarUploading, setAvatarUploading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/sports/public/open-play-rsvp/${token}`)
      .then(r => {
        setData(r.data);
        const inv = r.data.invitation || {};
        setName(inv.name || '');
        setDivId(inv.division_id || '');
      })
      .catch(e => setError(errText(e, 'Invitation not found')))
      .finally(() => setLoading(false));
  }, [token]);

  const slug = data?.facility?.slug;
  const sid = data?.session?.id;

  const uploadAvatar = async (file) => {
    if (!file || !slug || !sid) return;
    setAvatarUploading(true);
    const fd = new FormData(); fd.append('file', file);
    try {
      const r = await axios.post(`${API}/sports/public/${slug}/open-play/${sid}/upload-avatar`, fd);
      setAvatarKey(r.data.avatar_key); setAvatarThumbKey(r.data.avatar_thumb_key);
      setAvatarPreview(URL.createObjectURL(file));
    } catch { setError('Avatar upload failed — try a PNG or JPG'); }
    finally { setAvatarUploading(false); }
  };

  const acceptWithDetails = async () => {
    if (!name.trim()) { setError('Enter your name'); return; }
    if (data.session.divisions?.length > 0 && !divId) { setError('Please select a division'); return; }
    setResponding(true); setError('');
    try {
      const r = await axios.post(`${API}/sports/public/open-play-rsvp/${token}/respond`, {
        response: 'accept',
        name: name.trim(),
        phone: phone.trim(),
        org_label: orgLabel.trim() || null,
        division_id: divId || null,
        avatar_key: avatarKey,
        avatar_thumb_key: avatarThumbKey,
      });
      setDone({ ...r.data, personal_token: r.data.personal_token, slug: r.data.facility_slug });
      setData(prev => prev ? { ...prev, invitation: { ...prev.invitation, status: 'accepted' } } : prev);
    } catch (e) {
      setError(errText(e, 'Failed to respond'));
    } finally {
      setResponding(false);
    }
  };

  const respond = async (response) => {
    setResponding(true);
    try {
      const r = await axios.post(`${API}/sports/public/open-play-rsvp/${token}/respond`, { response });
      setDone(r.data);
      setData(prev => prev ? { ...prev, invitation: { ...prev.invitation, status: r.data.status } } : prev);
    } catch (e) {
      setError(errText(e, 'Failed to respond'));
    } finally {
      setResponding(false);
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#0A0A0A', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="animate-spin w-8 h-8 rounded-full" style={{ border: `2px solid ${RED}`, borderTopColor: 'transparent' }} />
      </div>
    );
  }

  if (error && !data) {
    return (
      <div style={{ minHeight: '100vh', background: '#0A0A0A', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', fontFamily: 'Manrope, sans-serif', padding: 20 }}>
        <AlertCircle style={{ width: 48, height: 48, color: '#EF4444', marginBottom: 16 }} />
        <h1 style={{ fontFamily: 'Oswald, sans-serif', fontSize: 24, marginBottom: 8 }}>Invitation Not Found</h1>
        <p style={{ color: '#737373', fontSize: 14, textAlign: 'center' }}>{error}</p>
      </div>
    );
  }

  const inv = data.invitation;
  const sess = data.session;
  const facility = data.facility;
  const st = STATUS_UI[inv.status] || STATUS_UI.pending;
  const StIcon = st.icon;
  const isPending = inv.status === 'pending';
  const fee = parseFloat(sess.entry_fee || 0);

  return (
    <div style={{ minHeight: '100vh', background: '#0A0A0A', color: '#fff', fontFamily: 'Manrope, sans-serif' }}>
      <header style={{ background: '#0D0D0D', borderBottom: '1px solid #1A1A1A', padding: '16px 20px' }}>
        <div style={{ maxWidth: 600, margin: '0 auto', display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 36, height: 36, borderRadius: 8, background: RED, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Zap style={{ width: 20, height: 20, color: '#0A0A0A' }} />
          </div>
          <div>
            <p style={{ fontFamily: 'Oswald, sans-serif', fontSize: 14, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: RED }}>
              Open Play Invitation
            </p>
            <p style={{ fontSize: 12, color: '#555' }}>{facility.name}</p>
          </div>
        </div>
      </header>

      <div style={{ maxWidth: 600, margin: '0 auto', padding: '32px 20px 60px' }}>
        <div data-testid="op-rsvp-status-badge" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: st.bg, color: st.color, borderRadius: 20, padding: '6px 14px', fontSize: 12, fontWeight: 700, marginBottom: 24 }}>
          <StIcon style={{ width: 14, height: 14 }} /> {st.label}
        </div>

        <div style={{ marginBottom: 24 }}>
          <p style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 2, color: '#555', marginBottom: 4 }}>Invited Player</p>
          <h1 data-testid="op-rsvp-name" style={{ fontFamily: 'Oswald, sans-serif', fontSize: 32, fontWeight: 900, textTransform: 'uppercase', letterSpacing: 1, color: '#fff' }}>
            {inv.name}
          </h1>
          <p style={{ fontSize: 13, color: '#555', marginTop: 4 }}>{inv.email}</p>
        </div>

        <div data-testid="op-rsvp-session-details" style={{ background: '#141414', border: '1px solid #1E1E1E', borderRadius: 14, padding: '20px 22px', marginBottom: 28 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
            <Zap style={{ width: 18, height: 18, color: RED }} />
            <h2 style={{ fontFamily: 'Oswald, sans-serif', fontSize: 20, fontWeight: 700, color: '#fff' }}>{sess.name}</h2>
          </div>
          {sess.description && <p style={{ fontSize: 13, color: '#888', marginBottom: 14, lineHeight: 1.6 }}>{sess.description}</p>}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {[
              ['Mode', (sess.game_mode || 'doubles').replace(/\b\w/g, c => c.toUpperCase()) + ' rotation'],
              ['Date', sess.date || 'TBA'],
              ['Time', `${sess.time_start || ''}${sess.time_end ? '–' + sess.time_end : ''}`],
              ...(fee > 0 ? [['Entry Fee', `₱${fee.toLocaleString()}`]] : []),
            ].map(([k, v]) => (
              <div key={k} style={{ background: '#0A0A0A', borderRadius: 8, padding: '8px 12px' }}>
                <p style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 1, color: '#555' }}>{k}</p>
                <p style={{ fontSize: 13, fontWeight: 600, color: '#ccc', marginTop: 2 }}>{v}</p>
              </div>
            ))}
          </div>
          {(sess.contact_email || sess.contact_phone) && (
            <p data-testid="op-rsvp-contact" style={{ fontSize: 12, color: '#777', marginTop: 14 }}>
              Questions? Contact {sess.contact_name || 'the organizer'}
              {sess.contact_email && <> · <a href={`mailto:${sess.contact_email}`} style={{ color: RED }}>{sess.contact_email}</a></>}
              {sess.contact_phone && <> · {sess.contact_phone}</>}
            </p>
          )}
        </div>

        {isPending && inv.expires_at && (
          <p style={{ fontSize: 12, color: '#555', marginBottom: 20 }}>
            <Clock style={{ width: 12, height: 12, display: 'inline', marginRight: 4 }} />
            Respond before {new Date(inv.expires_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </p>
        )}

        {error && <p style={{ color: '#EF4444', fontSize: 13, marginBottom: 16 }}>{error}</p>}

        {isPending && !done && !showForm && (
          <div data-testid="op-rsvp-actions" className="flex gap-3">
            <button data-testid="op-rsvp-accept-btn" onClick={() => { setError(''); setShowForm(true); }} disabled={responding}
              style={{ flex: 2, padding: '14px', borderRadius: 10, border: 'none', background: '#22C55E', color: '#0A0A0A', fontSize: 15, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 1, cursor: responding ? 'not-allowed' : 'pointer', fontFamily: 'Oswald, sans-serif', opacity: responding ? 0.6 : 1 }}>
              Accept Invitation
            </button>
            <button data-testid="op-rsvp-decline-btn" onClick={() => respond('decline')} disabled={responding}
              style={{ flex: 1, padding: '14px', borderRadius: 10, border: '1px solid #2A2A2A', background: '#141414', color: '#EF4444', fontSize: 14, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, cursor: responding ? 'not-allowed' : 'pointer', fontFamily: 'Oswald, sans-serif' }}>
              Decline
            </button>
          </div>
        )}

        {isPending && !done && showForm && (
          <div data-testid="op-rsvp-accept-form" style={{ background: '#141414', border: '1px solid #1E1E1E', borderRadius: 14, padding: '20px 22px' }}>
            <p style={{ fontFamily: 'Oswald, sans-serif', fontSize: 16, fontWeight: 800, color: '#22C55E', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 14 }}>Confirm Your Details</p>

            <label style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, color: '#777' }}>Full Name</label>
            <input data-testid="op-rsvp-name-input" value={name} onChange={e => setName(e.target.value)} placeholder="Your name *" style={{ ...inputStyle, marginTop: 4 }} />

            <label style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, color: '#777' }}>Phone (optional)</label>
            <input data-testid="op-rsvp-phone-input" value={phone} onChange={e => setPhone(e.target.value)} placeholder="For SMS check-in reminders" style={{ ...inputStyle, marginTop: 4 }} />

            <label style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, color: '#777' }}>Representing (optional)</label>
            <input data-testid="op-rsvp-org-input" value={orgLabel} onChange={e => setOrgLabel(e.target.value)} placeholder="Your organization / club, or leave blank" style={{ ...inputStyle, marginTop: 4 }} />
            <p style={{ fontSize: 11, color: '#555', marginTop: -6, marginBottom: 10 }}>
              Represent yourself or your org — this shows on the roster and leaderboard.
            </p>

            {sess.divisions?.length > 0 && (
              <>
                <label style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, color: '#777' }}>Division</label>
                <select data-testid="op-rsvp-division" value={divId} onChange={e => setDivId(e.target.value)} style={{ ...inputStyle, marginTop: 4, cursor: 'pointer' }}>
                  <option value="">Select division *</option>
                  {sess.divisions.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </>
            )}

            <label style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, color: '#777' }}>Avatar (optional)</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 6 }}>
              <div style={{ width: 52, height: 52, borderRadius: 26, border: '1px solid #2A2A2A', overflow: 'hidden', flexShrink: 0, background: '#0A0A0A', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {avatarPreview ? <img src={avatarPreview} alt="avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span style={{ fontSize: 10, color: '#525252' }}>Photo</span>}
              </div>
              <input data-testid="op-rsvp-avatar-upload" type="file" accept="image/*" onChange={e => uploadAvatar(e.target.files?.[0])} style={{ color: '#A3A3A3', fontSize: 12 }} />
            </div>
            <p style={{ fontSize: 11, color: '#555', marginTop: 4 }}>Upload your face OR your organization&apos;s logo — your call.</p>
            {avatarUploading && <p style={{ fontSize: 12, color: RED, marginTop: 6 }}>Uploading…</p>}
            {avatarThumbKey && !avatarUploading && <p style={{ fontSize: 12, color: '#22C55E', marginTop: 6 }}>✓ Avatar attached</p>}

            {error && <p style={{ color: '#EF4444', fontSize: 13, marginTop: 12 }}>{error}</p>}

            <button data-testid="op-rsvp-confirm-btn" onClick={acceptWithDetails} disabled={responding || avatarUploading}
              style={{ width: '100%', marginTop: 16, padding: '14px', borderRadius: 10, border: 'none', background: '#22C55E', color: '#0A0A0A', fontSize: 15, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 1, cursor: (responding || avatarUploading) ? 'not-allowed' : 'pointer', fontFamily: 'Oswald, sans-serif', opacity: (responding || avatarUploading) ? 0.6 : 1 }}>
              {responding ? 'Confirming…' : 'Confirm & Join'}
            </button>
            <button onClick={() => { setShowForm(false); setError(''); }} disabled={responding}
              style={{ width: '100%', marginTop: 8, padding: '10px', borderRadius: 10, border: '1px solid #2A2A2A', background: 'transparent', color: '#777', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
              Back
            </button>
          </div>
        )}

        {(done?.status === 'accepted' || inv.status === 'accepted') && (
          <div data-testid="op-rsvp-accepted-msg" style={{ textAlign: 'center', padding: '24px 0' }}>
            <CheckCircle style={{ width: 48, height: 48, color: '#22C55E', margin: '0 auto 12px' }} />
            <h2 style={{ fontFamily: 'Oswald, sans-serif', fontSize: 24, color: '#22C55E', marginBottom: 8 }}>You&apos;re In!</h2>
            <p style={{ fontSize: 14, color: '#888' }}>
              <strong style={{ color: '#fff' }}>{inv.name}</strong> is confirmed for {sess.name}.
              Use your active email <strong style={{ color: '#ccc' }}>{inv.email}</strong> for check-in on game day.
            </p>
            {done?.personal_token && (
              <a href={`/sports/open-play/player/${done.personal_token}`}
                style={{ display: 'inline-block', marginTop: 16, padding: '10px 24px', borderRadius: 8, background: RED, color: '#fff', textDecoration: 'none', fontSize: 13, fontWeight: 700 }}>
                My Player Page →
              </a>
            )}
          </div>
        )}
        {(done?.status === 'declined' || inv.status === 'declined') && !isPending && (
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <XCircle style={{ width: 48, height: 48, color: '#EF4444', margin: '0 auto 12px' }} />
            <h2 style={{ fontFamily: 'Oswald, sans-serif', fontSize: 24, color: '#EF4444', marginBottom: 8 }}>Invitation Declined</h2>
            <p style={{ fontSize: 14, color: '#888' }}>The slot has been released.</p>
          </div>
        )}
        {inv.status === 'expired' && (
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <AlertCircle style={{ width: 48, height: 48, color: '#737373', margin: '0 auto 12px' }} />
            <h2 style={{ fontFamily: 'Oswald, sans-serif', fontSize: 24, color: '#737373', marginBottom: 8 }}>Invitation Expired</h2>
            <p style={{ fontSize: 14, color: '#555' }}>This invitation is no longer valid.</p>
          </div>
        )}

        <p style={{ textAlign: 'center', fontSize: 11, color: '#333', marginTop: 40 }}>
          Powered by <a href="/" style={{ color: RED, textDecoration: 'none', fontWeight: 700 }}>AgriBooks Sports</a>
        </p>
      </div>
    </div>
  );
}
